import React from "react";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box } from "../../components/ui/box";
import { cn } from "../../lib/utils";
import type { Color } from "../../lib/colors";
import { DND_DRAGGABLE_PROPS } from "../../lib/dnd-attrs";
import { Icon } from "../../components/ui/icon";
import { TabsList, TabsTab } from "../../components/ui/tabs";
import type { IconName } from "../../components/ui/icon-registry";

/**
 * The drag-to-reorder tab row, and the colored icon chip its tabs carry.
 *
 * The library's `Tabs` is stock — it carries no ordering surface
 * and no pill-icon part — so the sortable behavior is app-side composition
 * around the stock list and the library stays untouched.
 *
 * Domain-free (values are opaque strings), so this lives in the particle tier
 * and is a promotion candidate for `packages/ui/src/particles/`.
 */

export type TabsPillIconProps = {
  /** Glyph shown inside the chip. */
  icon: IconName;
  /** Solid vibrant fill of the chip (glyph tint auto-contrasts). */
  color: Color;
};

/**
 * A 16px solid vibrant square with an auto-contrast glyph — white on saturated
 * fills, black on light ones. Render it as the first child of a `SortableTab`.
 */
export function TabsPillIcon({ icon, color }: TabsPillIconProps) {
  return (
    <Box
      width={16}
      height={16}
      align="center"
      justify="center"
      shrink={false}
      data-color={color}
      // Chip radius is not expressible as a Box prop; the fill and the glyph
      // foreground both come off the color name declared here.
      className="rounded-[3px] bg-c"
    >
      {/* sm (14px) glyph in the 16px chip: the dense, nearly-flush look. The
          heavier stroke keeps small glyphs legible on the solid fill. */}
      <Icon name={icon} size="sm" strokeWidth={2.75} className="text-c-foreground" />
    </Box>
  );
}

/** Set while a gesture crosses the drag threshold, so the click that follows a
 *  drop does not also select the tab it landed on. Cleared on the next
 *  pointer-down — clearing it when `isDragging` flips back to false would race
 *  the click event that fires right after the drop. */
const DraggedRefContext = React.createContext<React.RefObject<boolean> | null>(
  null,
);

export type SortableTabsListProps = {
  /** Draggable tab values in display order. The consumer renders its tabs in
   *  this same order. */
  order: string[];
  /** Fires with the full reordered array after a drop. */
  onOrderChange: (order: string[]) => void;
  children: React.ReactNode;
};

export function SortableTabsList({
  order,
  onOrderChange,
  children,
}: SortableTabsListProps) {
  const draggedRef = React.useRef(false);

  // A distance constraint keeps a plain click a click: only a gesture that
  // travels past the threshold becomes a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = React.useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) return;
      const from = order.indexOf(String(active.id));
      const to = order.indexOf(String(over.id));
      if (from === -1 || to === -1) return;
      onOrderChange(arrayMove(order, from, to));
    },
    [order, onOrderChange],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={order} strategy={horizontalListSortingStrategy}>
        <DraggedRefContext.Provider value={draggedRef}>
          <TabsList variant="underline">{children}</TabsList>
        </DraggedRefContext.Provider>
      </SortableContext>
    </DndContext>
  );
}

export type SortableTabProps = {
  value: string;
  children: React.ReactNode;
};

/** A stock `TabsTab` that is also the drag source. The whole tab drags — there
 *  is no separate grip, because the tab has no gutter to host one (a sidebar
 *  nav row works the same way). */
export function SortableTab({ value, children }: SortableTabProps) {
  const draggedRef = React.useContext(DraggedRefContext);
  const {
    // Strip `role` (the tab underneath is the real `tab`) and `tabIndex`
    // (Base UI's roving focus owns the tab stops — a second one would add a
    // stray stop).
    attributes: { role: _role, tabIndex: _tabIndex, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({ id: value });

  React.useEffect(() => {
    if (isDragging && draggedRef) draggedRef.current = true;
  }, [isDragging, draggedRef]);

  return (
    <TabsTab
      ref={setNodeRef}
      value={value}
      style={{
        // Translate only, never scale: the sortable transform scales the active
        // item toward the size of the slot it hovers over, which squashes a
        // wide tab dragged over a narrow one. Pin y to 0 — the row only
        // reorders horizontally; vertical jitter reads as breakage.
        transform:
          CSS.Translate.toString(transform ? { ...transform, y: 0 } : null) ??
          undefined,
        transition: isSorting ? transition : undefined,
        // Transformed flex items overlap while dragging; lift the active one so
        // it renders above the tabs it crosses.
        zIndex: isDragging ? 10 : undefined,
        cursor: isDragging ? "grabbing" : undefined,
      }}
      onClickCapture={(event: React.MouseEvent) => {
        if (draggedRef?.current) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      {...attributes}
      {...listeners}
      {...DND_DRAGGABLE_PROPS}
      // AFTER the spread, and forwarding to the sensor's own handler: the
      // drag listeners include `onPointerDown`, so a reset declared above
      // would be overwritten and the flag would latch on forever — one drag
      // and the tab could never be clicked again.
      onPointerDown={(event: React.PointerEvent) => {
        if (draggedRef) draggedRef.current = false;
        listeners?.onPointerDown?.(event);
      }}
    >
      {children}
    </TabsTab>
  );
}
